import { useDocumentVisibility, useEventListener, useEventSource } from '@vueuse/core';
import { onUnmounted, ref, watch } from 'vue';
import {
  type GameChangedNotification,
  type NotificationFrame,
  type NotificationTopic,
  readStreamRefusal,
  type StreamNotification,
  type TimeNotification,
  type TraceNotification,
} from '../../../shared/api/notification.ts';
import { sseLogger } from '../../../shared/observability/sse.ts';
import { useResyncView } from './useGameView.ts';

const NOTIFICATIONS_URL = '/api/game/notifications';

export function useNotifications() {
  const resyncView = useResyncView();
  const { eventSource, status, error, open, close } = useEventSource(NOTIFICATIONS_URL, [], {
    autoReconnect: true,
    withCredentials: true,
    immediate: false,
  });
  const sseLog = sseLogger(NOTIFICATIONS_URL);
  watch(status, (now) => sseLog.onStatus(now, error.value));
  onUnmounted(close);

  const secondsLeft = ref<string>('');

  function onNotification<N extends StreamNotification>(
    topic: NotificationTopic,
    listener: (frame: NotificationFrame<N>) => void,
  ) {
    useEventListener(eventSource, topic, listener);
  }
  onNotification<GameChangedNotification>('GAME_CHANGED', resyncView);
  onNotification<TimeNotification>('TIME', (frame) => {
    secondsLeft.value = frame.data;
  });
  onNotification<TraceNotification>('TRACE', (frame) => sseLog.onTrace(frame.data));

  const refusal = ref('');
  watch(error, async (failed) => {
    if (failed) refusal.value = await readStreamRefusal().catch(() => '');
  });

  const connectionLost = ref(false);
  let hasOpened = false;
  watch(status, (now) => {
    if (now === 'OPEN') {
      hasOpened = true;
      refusal.value = '';
      connectionLost.value = false;
      void resyncView();
    } else if (hasOpened) {
      connectionLost.value = true;
    }
  });
  useEventListener(window, 'offline', () => {
    if (hasOpened) connectionLost.value = true;
  });

  function reopen() {
    if (status.value === 'CONNECTING') return;
    open();
  }

  useEventListener(window, 'online', reopen);

  const visibility = useDocumentVisibility();
  watch(visibility, (now) => {
    if (now !== 'visible') return;
    reopen();
    void resyncView();
  });

  return { status, connectionLost, refusal, secondsLeft, open, close };
}
